import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  User,
  ArrowLeft,
  Search,
  Dumbbell,
  ChevronRight,
  Mail,
  Calendar,
  Target,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LinkButton } from '../components/ui/link-button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { API } from '../utils/api';

export const UserWorkoutsList = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Filter only participants (not admins)
      const participants = response.data.filter(u => u.role === 'participant');
      setUsers(participants);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Acesso Negado</h2>
          <p className="text-zinc-400">Você não tem permissão para acessar esta área.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-lime"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 md:px-6 pt-6 md:pt-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <LinkButton
          to="/admin"
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-white p-2"
        >
          <ArrowLeft className="w-6 h-6" />
        </LinkButton>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Dumbbell className="w-8 h-8 text-neon-lime" />
            <h1 className="font-primary font-black text-3xl md:text-4xl text-white uppercase tracking-tighter">
              Treinos por Aluno
            </h1>
          </div>
          <p className="text-zinc-400 text-sm md:text-base mt-1">
            Selecione um aluno para gerenciar seus treinos individuais
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <Input
          type="text"
          placeholder="Buscar aluno por nome ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900/50 border-zinc-800 focus:border-neon-lime text-white h-12"
        />
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 text-center">
          <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="font-primary font-bold text-xl text-white mb-2">
            {searchTerm ? 'Nenhum aluno encontrado' : 'Nenhum aluno cadastrado'}
          </h2>
          <p className="text-zinc-500">
            {searchTerm 
              ? 'Tente buscar por outro nome ou email.' 
              : 'Cadastre alunos no painel de administração.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map((u, index) => (
            <motion.div
              key={u.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${u.is_active ? 'bg-neon-lime' : 'bg-zinc-700'}`}>
                    <User className={`w-6 h-6 ${u.is_active ? 'text-black' : 'text-zinc-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg">
                      {u.name}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {u.email}
                      </span>
                      <span className="flex items-center gap-1" data-testid={`meta-${u.id}`}>
                        <Target className="w-3 h-3" />
                        <span className={`font-medium ${(u.workouts_this_month || 0) >= (u.monthly_goal || 4) ? 'text-neon-lime' : 'text-zinc-400'}`}>
                          {u.workouts_this_month || 0}/{u.monthly_goal || 4}
                        </span>
                        <span className="hidden sm:inline">treinos</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <LinkButton
                    to={`/admin/student/${u.id}/calendar`}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-400 hover:text-neon-lime hover:border-neon-lime/30 gap-1.5"
                    data-testid={`cal-btn-${u.id}`}
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden sm:inline">Calendário</span>
                  </LinkButton>
                  <LinkButton
                    to={`/admin/users/${u.id}/workouts`}
                    size="sm"
                    className="bg-neon-lime text-black hover:bg-neon-lime/80 font-bold gap-1.5"
                    data-testid={`manage-btn-${u.id}`}
                  >
                    <Dumbbell className="w-4 h-4" />
                    <span className="hidden sm:inline">Gerenciar</span>
                  </LinkButton>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 text-center">
        <p className="text-zinc-600 text-sm">
          {filteredUsers.length} aluno(s) {searchTerm && 'encontrado(s)'}
        </p>
      </div>
    </div>
  );
};
